import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Languages, Star, Quote, TrendingUp } from "lucide-react";
import { useLang } from "@/i18n";
import { STORIES } from "@/data/storiesData";
import WhatsAppFloat from "@/components/site/WhatsAppFloat";

const Stars = () => (
  <div className="flex gap-1" data-testid="story-stars">
    {[...Array(5)].map((_, i) => (
      <Star key={i} size={16} className="fill-gold text-gold" />
    ))}
  </div>
);

export default function SuccessStories() {
  const { t, lang, setLang, dir } = useLang();
  const Arrow = dir === "rtl" ? ArrowRight : ArrowLeft;

  useEffect(() => {
    document.title = `${t("stories_title")} | ${t("brand")} ${t("tagline")}`;
    window.scrollTo(0, 0);
  }, [lang, t]);

  return (
    <div data-testid="success-stories-page" className="min-h-screen bg-navy-900 relative">
      <div className="noise-overlay" />
      <header className="fixed top-0 inset-x-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          <Link to="/" data-testid="stories-logo" className="flex items-center gap-3">
            <img src="/logo.png" alt="Barq Safe" className="h-11 w-11 object-contain rounded-xl bg-white/95 p-1" />
            <div className="leading-tight">
              <span className="block font-display font-extrabold">{t("brand")}</span>
              <span className="block text-[10px] tracking-widest text-gold">{t("tagline")}</span>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <button data-testid="stories-lang-toggle" onClick={() => setLang(lang === "ar" ? "en" : "ar")}
              className="flex items-center gap-2 px-3 py-2 rounded-full border border-gold/30 text-gold text-xs font-semibold hover:bg-gold/10 transition-colors">
              <Languages size={14} />
              {lang === "ar" ? "EN" : "عربي"}
            </button>
            <Link to="/" data-testid="stories-back-link"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/15 text-white/70 text-sm hover:border-gold/50 hover:text-gold transition-colors">
              <Arrow size={15} />
              {t("sp_back")}
            </Link>
          </div>
        </div>
      </header>

      <section className="relative pt-40 pb-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-navy-800/60 to-navy-900" />
        <div className="absolute -top-20 -start-20 w-96 h-96 rounded-full bg-gold/10 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <span className="inline-flex items-center gap-2 text-xs md:text-sm tracking-[0.3em] uppercase text-gold font-semibold mb-4">
              <TrendingUp size={15} />
              {t("story_quote_label")}
            </span>
            <h1 className="font-display font-black text-4xl md:text-5xl lg:text-6xl tracking-tight max-w-3xl" data-testid="stories-title">{t("stories_title")}</h1>
            <p className="mt-6 text-white/65 text-base md:text-lg leading-relaxed max-w-2xl" data-testid="stories-sub">{t("stories_sub")}</p>
          </motion.div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24 space-y-16">
        {STORIES.map((story, i) => (
          <motion.article
            key={story.id}
            data-testid={`story-${story.id}`}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="glass rounded-3xl p-8 md:p-12"
          >
            <div className="flex items-start justify-between gap-6 flex-wrap mb-8">
              <div className="flex items-center gap-4">
                <span className="font-display text-6xl font-black text-gold/20 leading-none select-none">{String(i + 1).padStart(2, "0")}</span>
                <div>
                  <h2 className="font-display font-extrabold text-2xl md:text-3xl">{story.name[lang]}</h2>
                  <p className="text-gold text-sm mt-1">{story.role[lang]}</p>
                  <p className="text-white/40 text-xs mt-0.5">{story.sector[lang]}</p>
                </div>
              </div>
              <Stars />
            </div>

            <blockquote className="relative glass rounded-2xl p-6 md:p-8 mb-8">
              <Quote size={24} className="text-gold/60 absolute -top-3 start-6" />
              <p className="text-white/85 text-base md:text-lg leading-relaxed italic">{story.quote[lang]}</p>
            </blockquote>

            <div className="grid md:grid-cols-2 gap-6 mb-10">
              <div>
                <h3 className="font-display font-bold text-lg text-gold mb-3">{t("story_challenge")}</h3>
                <p className="text-white/60 text-sm md:text-base leading-relaxed">{story.challenge[lang]}</p>
              </div>
              <div>
                <h3 className="font-display font-bold text-lg text-gold mb-3">{t("story_solution")}</h3>
                <p className="text-white/60 text-sm md:text-base leading-relaxed">{story.solution[lang]}</p>
              </div>
            </div>

            <div>
              <h3 className="font-display font-bold text-lg mb-5">{t("story_results")}</h3>
              <div className="grid grid-cols-3 gap-px bg-white/10 rounded-2xl overflow-hidden">
                {story.results[lang].map((r, j) => (
                  <div key={j} className="bg-navy-800/80 px-4 py-6 text-center" data-testid={`story-${story.id}-result-${j}`}>
                    <div className="font-display text-2xl md:text-4xl font-black gold-text">{r.n}</div>
                    <div className="mt-1.5 text-[11px] md:text-sm text-white/55 leading-snug">{r.l}</div>
                  </div>
                ))}
              </div>
            </div>
          </motion.article>
        ))}

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center pt-6"
        >
          <a href="/#contact" data-testid="stories-cta"
            className="inline-flex items-center gap-3 px-10 py-4 rounded-full bg-gold text-navy-900 font-bold hover:bg-gold-light transition-colors shadow-xl shadow-gold/20">
            {t("hero_cta1")}
          </a>
        </motion.div>
      </section>

      <div className="border-t border-gold/15 py-6 text-center text-xs text-white/40">© 2026 {t("footer_rights")}</div>
      <WhatsAppFloat />
    </div>
  );
}
